import { Customer, InputRuleApiModel } from '~/lib/api/smartsend/types';

/**
 * Boolean address rules render as a checkbox rather than an input, and are stored on the customer's Meta
 * bag rather than as a first-class property. These helpers are shared by the renderer
 * (CustomerSelectionValidationInput), the read/write mapping (CustomerSelection) and the
 * requirement-driven extra reconciler, so all three agree on what "on" means.
 */

export const IsBooleanRule = (rule: InputRuleApiModel): boolean => rule.type === 'boolean';

/**
 * Reads a customer field, preferring the Meta bag over a flat property.
 *
 * Quoting-driven address attributes that have no first-class property on Customer live in Meta, so a
 * Meta-first read is the only lookup that finds both. Mirrors the same precedence already used by
 * `Check` in CustomerConditionalHelper.ts (left as-is there rather than refactored, to avoid disturbing
 * the conditional-required evaluation).
 */
export const ReadCustomerField = (customer: Customer | undefined, name: string): string | undefined => {
  if (!customer) {
    return undefined;
  }

  return customer.Meta?.[name] ? customer.Meta[name]?.Value : (customer[name] as string | undefined);
};

/**
 * Whether a boolean rule is on, given the value stored against the address.
 *
 * An explicit stored value always wins; otherwise the server's `default` decides. The default is never
 * hardcoded here - the polarity is the courier's to set, and it differs between couriers, so a rule
 * arriving with `default: 'false'` must render unticked.
 *
 * Both sides are compared case-insensitively. The edge lowercases the default before sending it, but a
 * stored Meta value could have been written by any producer, and a .NET boolean stringifies as "True" -
 * so tolerating case here is what stops a stray capital silently reading as off.
 */
export const BooleanRuleIsOn = (stored: string | undefined, rule: InputRuleApiModel): boolean => {
  if (stored === undefined || stored === '') {
    return rule.default?.toLowerCase() === 'true';
  }

  return stored.toLowerCase() === 'true';
};

/** Convenience for the common case: read the stored value off the customer, then resolve it. */
export const CustomerBooleanRuleIsOn = (customer: Customer | undefined, rule: InputRuleApiModel): boolean =>
  BooleanRuleIsOn(ReadCustomerField(customer, rule.name), rule);
